import { Paginator } from "primereact/paginator";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { CommunicationLogItem } from "../components/CommunicationLogItem";
import { CommunicationLogDialogMeta } from "../components/CommunicationLogDialogMeta";
import { MessageStatus } from "./models/emailLogModel";
import { useEmailLogsList } from "./hooks/useEmailLogsList";
import { DataNotice } from "../../../../common/components/dataNotice/DataNotice";
import { useAppContext } from "../../../../AppContext";
import styles from "./EmailLogsList.module.scss";

import { dvSize } from "../../../../common/infrastructure/utilities/viewportUnitsUtility";
const EMAIL_LOGS_CUTOFF_DATE = new Date(2026, 2, 6); // 06.03.2026

interface IEmailLogsListProps {
  filterUserIds: number[] | null;
  filterEmailTypes: number[] | null;
}

export const EmailLogsList = ({ filterUserIds, filterEmailTypes }: IEmailLogsListProps) => {
  const { selectedStudio } = useAppContext();
  const {
    page,
    items,
    totalCount,
    isLoading,
    detail,
    isDetailLoading,
    isCancelling,
    isRetrying,
    selectedEmailId,
    setSelectedEmailId,
    handlePageChange,
    handleCancel,
    handleRetry,
    PAGE_SIZE,
  } = useEmailLogsList({ filterUserIds, filterEmailTypes });

  const showCutoffNotice =
    !selectedStudio ||
    new Date(selectedStudio.createdOn) < EMAIL_LOGS_CUTOFF_DATE;

  return (
    <div className={styles.root}>
      <div className={styles.root_list}>
        {isLoading &&
          Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className={styles.root_list_skeleton} />
          ))}

        {!isLoading && items.length === 0 && (
          <div className={styles.root_empty}>
            <span>Няма намерени имейли.</span>
            {showCutoffNotice && (
              <span className={styles.root_empty_note}>
                Записите обхващат имейли, изпратени след 07.03.2026 г.
              </span>
            )}
          </div>
        )}

        {!isLoading &&
          items.map((item) => (
            <CommunicationLogItem
              key={item.id}
              index={item.index}
              receiverName={item.receiverName}
              receiver={item.receiverEmail}
              details={item.subject}
              status={item.status}
              createdOn={item.createdOn}
              onClick={() => setSelectedEmailId(item.id)}
            />
          ))}

        {showCutoffNotice &&
          !isLoading &&
          items.length > 0 &&
          page >= Math.ceil(totalCount / PAGE_SIZE) && (
            <DataNotice>
              Записите обхващат имейли, изпратени след{" "}
              <strong>06.03.2026 г.</strong>
            </DataNotice>
          )}
      </div>

      {totalCount > PAGE_SIZE && (
        <Paginator
          first={(page - 1) * PAGE_SIZE}
          rows={PAGE_SIZE}
          totalRecords={totalCount}
          onPageChange={handlePageChange}
          className={styles.root_paginator}
        />
      )}

      <AppDialog
        visible={!!selectedEmailId}
        onHide={() => setSelectedEmailId(null)}
        header={detail?.subject ?? "Имейл"}
        style={{ width: "90vw", maxWidth: "800px", height: dvSize("90dvh") }}
        contentClassName={styles.dialog_scrollable_content}
      >
        {isDetailLoading ? (
          <div className={styles.dialog_loading}>Зареждане...</div>
        ) : (
          <div className={styles.dialog_content}>
            <CommunicationLogDialogMeta
              receiver={detail?.receiverEmail}
              status={detail?.status}
              errorMessage={detail?.errorMessage}
            />

            <div
              className={styles.dialog_body}
              dangerouslySetInnerHTML={{ __html: detail?.compiledBody ?? "" }}
            />

            <RenderWhen condition={detail?.status === MessageStatus.Pending}>
              <div className={styles.dialog_actions}>
                <DefaultButton
                  label="Отмени изпращане"
                  design="secondary"
                  onClick={handleCancel}
                  loading={isCancelling}
                  width="auto"
                />
              </div>
            </RenderWhen>

            <RenderWhen condition={detail?.status === MessageStatus.Failed}>
              <div className={styles.dialog_actions}>
                <DefaultButton
                  label="Повтори изпращане"
                  design="default"
                  onClick={handleRetry}
                  loading={isRetrying}
                  width="auto"
                />
              </div>
            </RenderWhen>
          </div>
        )}
      </AppDialog>
    </div>
  );
};
